<template>
  <div class="gs-app gm-sequences-refresh">
    <!-- Loader -->
    <portal to="loaderTarget">
      <div class="absolute bg-grey-100 bg-opacity-50 h-full left-0 top-0 w-full z-99" v-if="isLoading">
        <gd-loader :label="$t('frontend_loading')"/>
      </div>
    </portal>
    
    <div class="flex mb-4 mx-4">
      <div class="w-full gm-sequences-refresh__workspace">
        <!-- Can mail/Upgraded user/Sandbox mode check -->
        <div
          class="w-full btn-radius bg-white text-grey-700 p-4 mb-2"
          style="box-shadow: 10px 5px 10px 0 rgba(22, 29, 37, 0.05)"
          v-if="showSenderBanner && this.userCanMail"
          :style="{display: hideBanner ? 'none' : 'block',}"
        >
          <create-sender-banner
            @closeSenderBanner="closeSenderBanner"
            @smsSenderCreated="closeSenderBanner"
          ></create-sender-banner>
        </div>
        <!-- Can mail/Upgraded user/Sandbox mode check -->

        <navigation-tabs
          v-if="showNavTabs && showNavigation"
          class="gm-nav-sequences-tabs absolute"
          :class="{
            'gm-nav-sequences-tabs-no-sender':
              showSenderBanner && this.userCanMail && !hideBanner,
          }"
          :nav_tabs="navTabs"
        ></navigation-tabs>

        <client-table
          class="groovemail-sequence-table gs-refresh-controls"
          :tableRef="'sequenceTable'"
          :tableData="mailsequences"
          :tableColumns="mail_listsTableColumns"
          :tableOptions="mail_listsTableOptions"
          :tableTitle="'frontend_groovemail_sequences_route_page_title'"
          :tableCondition="mailsequences.length"
          :slotData="slotData"
          :emptyStateData="emptyStateData"
          :addNewBtnText="'frontend_groovemail_sequences_index_new_sequence'"
          @addNewData="openCreateMailsequence"
        >
        </client-table>

        <gs-named-modal class="gs-refresh-controls"
          name="create-mailsequence-modal"
          :title="$t('frontend_groovemail_sequences_configmenu_create_new')"
        >
          <div class="p-6">
            <div class="flex flex-col mb-6">
              <label class="font-semibold text-base text-dark mb-2">
                {{ $t('frontend_groovemail_adminsetting_name') }}
              </label>
              <text-input
                variant="base"
                v-model="model.name"
                :placeholder="$t('frontend_groovemail_adminsetting_name')"
                :validator="$v.model.name"
              />
            </div>
            <div class="flex flex-col mb-6">
              <label class="font-semibold text-base text-dark mb-2">
                {{ $t('frontend_groovemail_adminsetting_description') }}
              </label>
              <text-input
                variant="base"
                v-model="model.description"
                :placeholder="$t('frontend_groovemail_adminsetting_description')"
                :validator="$v.model.description"
              />
            </div>

            <div class="flex justify-end">
              <gd-button
                size="auto"
                @click="closeCreateMailsequence"
                >{{ $t('frontend_cancel') }}</gd-button
              >
              <gd-button
                class="ml-2"
                size="auto"
                variant="primary"
                @click="createMailSequence"
              >
                {{ $t('frontend_groovemail_sequences_index_create') }}
              </gd-button>
            </div>
          </div>
        </gs-named-modal>

        <gs-named-modal class="gs-refresh-controls"
          name="edit-mailsequence-modal"
          :title="
            actionType
              ? $t('frontend_groovemail_sequence_clone_the_sequence')
              : $t('frontend_groovemail_sequence_rename_the_sequence')
          "
        >
          <div class="p-6">
            <div class="flex flex-col mb-6">
              <label class="font-semibold text-base text-dark mb-2">
                {{ $t('frontend_groovemail_adminsetting_name') }}
              </label>
              <text-input
                variant="base"
                v-model="model.name"
                :placeholder="$t('frontend_groovemail_adminsetting_name')"
                :validator="$v.model.name"
              />
            </div>

            <div class="flex flex-col mb-6">
              <label class="font-semibold text-base text-dark mb-2">
                {{ $t('frontend_groovemail_adminsetting_description') }}
              </label>
              <text-input
                variant="base"
                v-model="model.description"
                :placeholder="$t('frontend_groovemail_adminsetting_description')"
                :validator="$v.model.description"
              />
            </div>

            <div class="flex justify-end">
              <gd-button
                size="auto"
                @click="closerenameMailsequence"
                >{{ $t('frontend_cancel') }}</gd-button
              >
              <gd-button
                class="ml-2"
                size="auto"
                variant="primary"
                @click="
                  actionType ? openClonedMailsequence() : editMailsequence()
                "
              >
                {{
                  actionType
                    ? $t('frontend_clone')
                    : $t('frontend_groovemail_sequences_index_update')
                }}
              </gd-button>
            </div>
          </div>
        </gs-named-modal>
      </div>
    </div>

    <!-- Confirmation delete modal -->
    <confirm-modal
      :ref="`confirmation-delete-sequence`"
      :name="`confirmation-delete-sequence`"
      :buttonText="$t('frontend_confirm')"
      :cancelText="$t('frontend_cancel')"
      :confirmMessage="this.$t('frontend_bouses_delete_bonus_title')"
      :messageContent="this.$t('frontend_groovemail_swal_revert_message')"
      @confirm="deleteMailsequence"
      @close="closeDeleteSequenceModal"
    />
  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import { required, minLength, maxLength } from 'vuelidate/lib/validators';

import ClientTable from '@groovepages/gd-ui-kit/components/tables/ClientTable.vue';
import Mailsequence from '@/models/Mailsequence';
import themeConfig from '@/theme/themeConfig.js';

import CreateSenderBanner from '@/components/createSenderBanner.vue';

import showAlert from '@/components/sweet-alert-popup.js';
import NavigationTabs from '@/components/navigation-tabs.vue';

export default {
  middleware: 'auth',
  props: { showNavigation: { type: Boolean, default: true } },

  metaInfo() {
    return { title: this.$t('frontend_groovemail_lead_detail_sequence_tab') };
  },

  data() {
    return {
      showNavTabs: true,
      userCanMail: null,
      actionType: 0,
      mailsequences: [],
      hideBanner: false,
      showSenderBanner: false,
      timer: '',
      model: {
        name: '',
        description: '',
      },
      emptyStateData: {
        emptyStateHeading: 'frontend_groovemail_lead_detail_sequence_tab',
        emptyStateSubHeading: 'frontend_groovemail_sequences_index_you',
        emptyStateBtnText: 'frontend_groovemail_sequences_index_new_sequence',
      },
      fullPageLoader: true,
      isLoading: false,
      loaderColor: themeConfig.appPrimaryColor,
      navTabs: [
        {
          tabName:
            'frontend_groovemail_components_pricepoint_sidebar_broadcasts',
          tabRoute: 'Broadcasts',
        },
        {
          tabName:
            'frontend_groovemail_components_pricepoint_sidebar_sequences',
          tabRoute: 'Sequences',
        },
        {
          tabName: 'frontend_groovemail_admin_global_templates_page_header',
          tabRoute: 'MyTemplates',
          tabQuery: { type: 'professionally-designed-templates' },
        },
        {
          tabName: 'frontend_groovemail_my_templates_page_header',
          tabRoute: 'MyTemplates',
          tabQuery: { type: 'email-templates' },
        },
      ],
      mail_listsTableColumns: ['name', 'triggered', 'status', 'actions'],
      mail_listsTableOptions: {
        sortIcon: {
          base: 'fa',
          is: 'fa-sort',
          up: 'fa-sort-asc',
          down: 'fa-sort-desc',
        },
        texts: {
          filterPlaceholder: this.$t('frontend_groovemail_tables_search_query'),
        },
        sortable: ['name', 'triggered'],
        filterable: ['name'],
        perPage: 10,
        debounce: 1000,
        alwaysShowPerPageSelect: true,
        perPageValues: [10, 25, 50, 100],
        headings: {
          name: () => this.$t('frontend_groovemail_adminsetting_name'),
          triggered: () => this.$t('frontend_groovemail_sequence_triggered'),
          status: () => this.$t('frontend_groovemail_campains_show_status'),
          actions: () => this.$t('frontend_groovemail_mail_lead_actions'),
        },
        rowClassCallback: () => 'hover:bg-grey-50 funnelRow',
      },

      sequenceId: null,
    };
  },

  validations: {
    model: {
      name: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(50),
      },
      description: {
        required,
        minLength: minLength(3),
      },
    },
  },

  components: {
    CreateSenderBanner,
    NavigationTabs,
    ClientTable,
  },

  methods: {
    //  Can mail/Upgraded user/Sandbox mode check
    ...mapGetters('mailing', ['getCanMail']),
    //  Can mail/Upgraded user/Sandbox mode check

    async goToFilteredLeads(sequence) {
      this.showNavTabs = false;
      this.$router.push({
        name: 'Leads2',
        query: {
          sequenceId: sequence.id,
          sequenceName: sequence.name,
        },
      });
    },

    openCreateMailsequence() {
      this.$root.$emit('modal-open', 'create-mailsequence-modal');
      this.model.name = '';
      this.model.description = '';
      this.$v.model.name.$reset();
      this.$v.model.description.$reset();
    },

    closeCreateMailsequence() {
      this.$root.$emit('modal-close', 'create-mailsequence-modal');
    },

    async renameMailsequence(seqId) {
      this.showLoader();
      this.actionType = 0;
      this.mailsequence = await Mailsequence.$find(seqId);
      this.model.name = this.mailsequence.name;
      this.model.description = this.mailsequence.description;
      this.$root.$emit('modal-open', 'edit-mailsequence-modal');
      this.hideLoader();
      
    },

    closerenameMailsequence() {
      this.$root.$emit('modal-close', 'edit-mailsequence-modal');
    },

    async cloneMailsequence(seqId) {
      this.showLoader();
      this.actionType = 1;
      this.mailsequence = await Mailsequence.$find(seqId);
      this.model.name = `${this.mailsequence.name} [ ${this.$t(
        'frontend_copy'
      ).toUpperCase()} ]`;
      this.model.description = this.mailsequence.description;
      this.$root.$emit('modal-open', 'edit-mailsequence-modal');
      this.hideLoader();
    },

    async openEditMailsequence(seqId) {
      this.showNavTabs = false;
      this.$router.push({
        name: 'Sequencestep',
        params: { id: seqId },
      });
    },

    async createMailSequence() {
      this.$v.model.name.$touch();
      this.$v.model.description.$touch();

      if (!this.$v.model.name.$invalid && !this.$v.model.description.$invalid) {
        this.showLoader();
        this.mailsequence = new Mailsequence({});
        this.mailsequence.name = this.model.name;
        this.mailsequence.description = this.model.description;
        this.mailsequence.viewtype = 'vertical';
        this.mailsequence.startruletags = JSON.stringify([]);
        this.mailsequence.startrulelists = JSON.stringify([]);
        this.mailsequence.startrulesegments = JSON.stringify([]);
        this.mailsequence.retedata = JSON.stringify([]);
        await this.mailsequence.save().then(response => {
          this.hideLoader();
          this.showNavTabs = false;
          this.$router.push({
            name: 'Sequencestep',
            params: { id: response.id },
          });
        }).catch(error => {
          // show error alert
          this.requestErrorAlert(error?.response?.data?.error);
        });
      }
    },

    fetchSequences(pageNum) {
      this.showLoader();
      axios
        .get('/mailsequence', { params: { page: pageNum } })
        .then((response) => {
          const result = response.data;
          this.mailsequences = result;
          this.hideLoader();
        })
        .catch(error => {
          // show error alert
          this.requestErrorAlert(error?.response?.data?.error);
        });
    },

    async fetchEventsList() {
      // show loader
      this.showLoader();

      try {
        this.mailsequences = await Mailsequence.$get();
      } catch (error) {
        // show error alert
        this.requestErrorAlert(error?.response?.data?.error);
      }
      
      // hide loader
      this.hideLoader();

    },

    async openClonedMailsequence() {
      this.$v.model.name.$touch();
      this.$v.model.description.$touch();

      if (!this.$v.model.name.$invalid && !this.$v.model.description.$invalid) {
        this.showLoader();

        const mailSequenceData = {
          name: this.model.name,
          description: this.model.description,
          sequence_id: this.mailsequence.id,
        };

        await axios.post('/sequence-clone', mailSequenceData)
          .then(response => {
            if (response && response.data && response.data.sequence_id) {
              this.showNavTabs = false;
              this.$router.push({
                name: 'Sequencestep',
                params: { id: response.data.sequence_id },
              });
            }

            this.hideLoader();
          })
          .catch(error => {
            // show error alert
            this.requestErrorAlert(error?.response?.data?.error);
          });
        
      }
    },

    async editMailsequence() {
      this.$v.model.name.$touch();
      this.$v.model.description.$touch();

      if (!this.$v.model.name.$invalid && !this.$v.model.description.$invalid) {
        this.showLoader();

        this.mailsequence.name = this.model.name;
        this.mailsequence.description = this.model.description;

        if (
          this.mailsequence.startruleforms &&
          typeof this.mailsequence.startruleforms === 'object'
        ) {
          this.mailsequence.startruleforms = JSON.stringify(
            this.mailsequence.startruleforms
          );
        }

        if (
          this.mailsequence.startrulelists &&
          typeof this.mailsequence.startrulelists === 'object'
        ) {
          this.mailsequence.startrulelists = JSON.stringify(
            this.mailsequence.startrulelists
          );
        }

        if (
          this.mailsequence.startrulesegments &&
          typeof this.mailsequence.startrulesegments === 'object'
        ) {
          this.mailsequence.startrulesegments = JSON.stringify(
            this.mailsequence.startrulesegments
          );
        }

        if (
          this.mailsequence.startruletags &&
          typeof this.mailsequence.startruletags === 'object'
        ) {
          this.mailsequence.startruletags = JSON.stringify(
            this.mailsequence.startruletags
          );
        }
        await this.mailsequence.save().then(async () => {
          this.$root.$emit('modal-close', 'edit-mailsequence-modal');
          this.mailsequences = await Mailsequence.$get();
          this.hideLoader();
        }).catch(error => {
          // show error alert
          this.requestErrorAlert(error?.response?.data?.error);
        }); 
      }
    },

    // open delete sequence modal
    async openDeleteSequenceModal(id) {
      // set the sequence id
      this.sequenceId = id;

      // open the modal
      this.$refs[`confirmation-delete-sequence`].open();
    },

    // close delete sequence modal
    closeDeleteSequenceModal() {
      // reset the sequence id
      this.sequenceId = null;

      // close the modal
      this.$refs[`confirmation-delete-sequence`].close();
    },

    async deleteMailsequence() {
      this.showLoader();

      try {
        const mailsequence = await Mailsequence.$find(this.sequenceId);
        await mailsequence.delete();
        const sequence = this.mailsequences.map((item) => item.id).indexOf(this.sequenceId);
        this.mailsequences.splice(sequence, 1);
        this.hideLoader();
      } catch(error) {
        // show error alert
        this.requestErrorAlert(error?.response?.data?.error);
      }
  
    },

    showLoader() {
      if (!this.isLoading) {
        this.isLoading = true;
      }
    },

    hideLoader() {
      if (this.isLoading) {
        this.isLoading = false;
      }
    },

    // error message for the request field
    async requestErrorAlert(error) {
      // hide the loader
      this.hideLoader();
      
      await showAlert({
        iconType: 'alert-error',
        html : `
          <style>.swal2-cancel { color: ${themeConfig.appPrimaryColorInvert} !important; }</style>
          <p class="text-sm text-deep-dark mb-3">${error || this.$t('frontend_backend_error') }</p>
        `,
        showCancelButton: false,
        showCloseButton: true,
        confirmButtonColor: themeConfig.appPrimaryColor,
        confirmButtonText: this.$t('frontend_close'),
      });
    },

    closeSenderBanner() {
      this.hideBanner = true;
    },

    async fetchSenderStatus() {
      this.showLoader();
      try {
        const sendersData = await axios.get('/email-sms-sender');
        if (sendersData.data.admin_setting && !sendersData.data.account_senders) {
          this.showSenderBanner = true;
        } else {
          this.showSenderBanner = false;
        }
      } catch (error) {
        // show error alert
        this.requestErrorAlert(error?.response?.data?.error);
      }
    },
  },

  async created() {
    // show loader
    this.showLoader();

    try {
      const dashboardData = await axios.get('/dashboard');
      this.userCanMail = dashboardData.data.can_mail;
      this.fetchSenderStatus();
      this.mailsequences = await Mailsequence.$get();
      
      
      if (this.$route.query.new && this.$route.query.new === 'modal') {
        this.openCreateMailsequence();
      }

      // hide loader
      this.hideLoader();
    } catch (error) {
      // show error alert
      this.requestErrorAlert(error?.response?.data?.error);
    }
  },

  computed: {
    admin() {
      return this.$store.state.user.admin;
    },

    slotData() {
      return [
        {
          slotName: 'name',
          slotType: 'default',
          showDescription: true,
          defaultVal: this.$t('frontend_groovemail_not_available'),
        },
        {
          slotName: 'triggered',
          slotType: 'clickable',
          callback: (props) => this.goToFilteredLeads(props.row),
        },
        {
          slotName: 'status',
          slotType: 'status',
          value: (props) => {
            const obj = {
              statusClasses: '',
              statusTitle: '',
            };
            if (!this.userCanMail) {
              obj.statusClasses = "border-danger text-danger";
              obj.statusTitle = 'frontend_groovemail_campains_step3_blocked';
            } else if (props.row.status === 1) {
              obj.statusClasses = 'border-success text-success';
              obj.statusTitle = 'frontend_groovemail_sequences_active';
            } else {
              obj.statusClasses = "border-warning text-warning";
              obj.statusTitle = 'frontend_groovemail_sequences_index_draft';
            }
            return obj;
          },
        },
        {
          slotName: 'actions',
          slotType: 'actions',
          actionList: [
            {
              id: 0,
              name: 'frontend_edit',
              conditions: () => true,
              callback: (props) => this.openEditMailsequence(props.row.id),
            },
            {
              id: 1,
              name: 'frontend_builder_pages_rename',
              conditions: () => true,
              callback: (props) => this.renameMailsequence(props.row.id),
            },
            {
              id: 2,
              name: 'frontend_clone',
              conditions: () => true,
              callback: (props) => this.cloneMailsequence(props.row.id),
            },
            {
              id: 3,
              name: 'frontend_delete',
              conditions: () => true,
              callback: (props) => this.openDeleteSequenceModal(props.row.id),
            },
          ],
        },
      ];
    },
  },
};
</script>

<style lang="scss">
.groovemail-sequence-table .gd-table {
  .VueTables--client table {
    th:nth-child(1),
    td:nth-child(1),
    th:nth-child(2),
    td:nth-child(3),
    th:nth-child(3),
    td:nth-child(3) {
      width: 28%;
    }
  }
}

.gm-nav-sequences-tabs {
  top: 37px;
  z-index: 10;
  
  &-no-sender {
    top: 93px;
    z-index: 10;
  }
}

</style>

<style scoped>
.gm-sequences-refresh {
  min-height: 100%;
  padding: 28px;
  color: var(--gs-foreground);
  background: var(--gs-background);
}

.gm-sequences-refresh__workspace {
  min-width: 0;
  padding: 20px 24px 24px;
  background: var(--gs-surface);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-lg);
  box-shadow: var(--gs-shadow-sm);
}

.gm-sequences-refresh >>> .VueTables__table,
.gm-sequences-refresh >>> .VueTables__table th,
.gm-sequences-refresh >>> .VueTables__table td {
  border-color: var(--gs-border);
}

@media (max-width: 640px) {
  .gm-sequences-refresh { padding: 18px 12px 32px; }
  .gm-sequences-refresh__workspace { padding: 16px; }
}
</style>
